import { ErrorView } from "@/components/ui/layout/error/errorView"
import type { BoxProgramDetailsEntity } from "@/modules/boxProgrammation/entities/boxProgramEntity"
import type { BoxSessionDetailsEntity } from "@/modules/boxSlotSession/entities/boxSessionEntity"
import type { UserProgramDetailsEntity } from "@/modules/programs/entities/userProgramDetailsEntity"
import { SessionBlockAthleteNoteEntity } from "@/modules/sessions/entities/sessionBlockAthleteNoteEntity"
import { SessionBlockCoachNoteEntity } from "@/modules/sessions/entities/sessionBlockCoachNoteEntity"
import { SessionBlockEntityFactory, type SessionBlockEntityType } from "@/modules/sessions/entities/sessionBlockEntity"
import { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import { SessionBlockMediaEntity } from "@/modules/sessions/entities/sessionBlockMediaEntity"
import type { BlockSessionInputDto } from "@/modules/sessions/gateway/sessionBlockDto"
import { ProgramSessionDetailsComponent } from "@/modules/sessions/sessionDetails/components/programSessionDetailsComponent"
import { SessionBlockType } from "@/modules/sessions/sessionDetails/sessionBlockTypes"
import { ExerciseForm } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/components/exerciseForm"
import type { ExerciseFormikType } from "@/modules/sessions/sessionForm/blocks/exerciseBlock/exerciseFormikTypes"
import { MediaForm } from "@/modules/sessions/sessionForm/blocks/mediaBlock/components/mediaForm"
import type { MediaBlockFormikType } from "@/modules/sessions/sessionForm/blocks/mediaBlock/mediaBlockFormikTypes"
import { NoteForAthletesForm } from "@/modules/sessions/sessionForm/blocks/noteBlock/components/noteForAthletesForm"
import { NoteForCoachesForm } from "@/modules/sessions/sessionForm/blocks/noteBlock/components/noteForCoachesForm"
import type {
  AthleteNoteFormikType,
  CoachNoteFormikType,
} from "@/modules/sessions/sessionForm/blocks/noteBlock/noteFormikTypes"
import { ProgramSessionFormWrapper } from "@/modules/sessions/sessionForm/components/programSessionFormWrapper"
import { LoadingSpinner } from "@/modules/ui/components/loading"
import { useBoxSlotSidebar } from "@modules/boxSlot/hooks/useBoxSlotSidebar"
import { word } from "@modules/core/utils/i18n"
import { ProgramInfiniteSessionEntity } from "@modules/sessions/entities/programInfiniteSessionEntity"
import { ProgramOnOffSessionEntity } from "@modules/sessions/entities/programOnOffSessionEntity"
import { type DetailedSessionType } from "@modules/sessions/sessionsType"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { type SidebarNavigatorParams } from "@modules/sidebar/sidebarTypes"
import { Suspense } from "react"
import { ErrorBoundary } from "react-error-boundary"

export const useSessionSidebar = () => {
  const { sidebarNavigateTo, hideSidebar, isSidebarVisible, sidebarNavigateBack, addSidebarStackOnTop } = useSidebar()
  const { navigateToBoxSlotView } = useBoxSlotSidebar()

  const navigateToSessionView = (
    session: DetailedSessionType,
    refreshCalendarEvents?: () => void,
    onHide?: () => Promise<boolean>,
  ) => {
    document.querySelector('[data-slot="right-sidebar-context"]')?.scrollTo({ top: 0 })

    return sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <ProgramSessionDetailsComponent
              editedSession={session}
              allowEditContent={true}
              refreshTraining={refreshCalendarEvents}
            />
          </Suspense>
        </ErrorBoundary>
      ),
      replaceAllComponents: true,
      onHide,
    })
  }

  const navigateToSessionForm = (
    program: UserProgramDetailsEntity | BoxProgramDetailsEntity,
    editedSession?: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity | BoxSessionDetailsEntity,
    dateSession?: string | number,
    editedBoxSlotId?: string,
    options: {
      refreshCalendarEvents?: () => void
      sidebarParams?: SidebarNavigatorParams
      replaceAllComponents?: boolean
    } = {
      replaceAllComponents: true,
    },
  ) => {
    document.querySelector('[data-slot="right-sidebar-context"]')?.scrollTo({ top: 0 })

    return sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <ProgramSessionFormWrapper
              program={program}
              editedSession={editedSession}
              dateSession={dateSession}
              editedBoxSlotId={editedBoxSlotId}
              onFinish={() => {
                if (options.refreshCalendarEvents) {
                  options.refreshCalendarEvents()
                }

                if (editedBoxSlotId) sidebarNavigateBack(false)
                else hideSidebar(false)
              }}
            />
          </Suspense>
        </ErrorBoundary>
      ),
      leftButtonText: word("global.return"),
      leftButtonAction: () => {
        if (editedBoxSlotId && options.refreshCalendarEvents) {
          void navigateToBoxSlotView(editedBoxSlotId, "session")
          return Promise.resolve(false)
        } else return Promise.resolve(true)
      },
      replaceAllComponents: options.replaceAllComponents,
      ...options.sidebarParams,
    })
  }

  const navigateToExerciseForm = (
    editedExercise: ExerciseFormikType | undefined,
    updatedExerciseIndex: number,
    onValidate: (exercise: ExerciseFormikType) => Promise<void>,
    args?: SidebarNavigatorParams,
  ) => {
    document.querySelector('[data-slot="right-sidebar-context"]')?.scrollTo({ top: 0 })
    document.querySelector('[data-slot="right-sidebar-content"]')?.scrollTo({ top: 0 })
    document.querySelector('[data-slot="right-sidebar-content-stack-on-top"]')?.scrollTo({ top: 0 })

    addSidebarStackOnTop({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <ExerciseForm
              editedExercise={editedExercise}
              updatedExerciseIndex={updatedExerciseIndex}
              onValidate={onValidate}
            />
          </Suspense>
        </ErrorBoundary>
      ),
      ...args,
    })
  }

  const navigateToNoteForCoachesForm = (
    updatedNote: CoachNoteFormikType | undefined,
    updatedNoteIndex: number,
    onValidate: (note: CoachNoteFormikType) => Promise<void>,
    args?: SidebarNavigatorParams,
  ) => {
    document.querySelector('[data-slot="right-sidebar-context"]')?.scrollTo({ top: 0 })
    document.querySelector('[data-slot="right-sidebar-content"]')?.scrollTo({ top: 0 })
    document.querySelector('[data-slot="right-sidebar-content-stack-on-top"]')?.scrollTo({ top: 0 })

    addSidebarStackOnTop({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <NoteForCoachesForm updatedNote={updatedNote} updatedNoteIndex={updatedNoteIndex} onValidate={onValidate} />
          </Suspense>
        </ErrorBoundary>
      ),
      ...args,
    })
  }

  const navigateToNoteForAthletesForm = (
    updatedNote: AthleteNoteFormikType | undefined,
    updatedNoteIndex: number,
    onValidate: (note: AthleteNoteFormikType) => Promise<void>,
    args?: SidebarNavigatorParams,
  ) => {
    document.querySelector('[data-slot="right-sidebar-context"]')?.scrollTo({ top: 0 })
    document.querySelector('[data-slot="right-sidebar-content"]')?.scrollTo({ top: 0 })
    document.querySelector('[data-slot="right-sidebar-content-stack-on-top"]')?.scrollTo({ top: 0 })

    addSidebarStackOnTop({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <NoteForAthletesForm
              updatedNote={updatedNote}
              updatedNoteIndex={updatedNoteIndex}
              onValidate={onValidate}
            />
          </Suspense>
        </ErrorBoundary>
      ),
      ...args,
    })
  }

  const navigateToMediaForm = (
    updatedMediaBlock: MediaBlockFormikType | undefined,
    updatedMediaBlockIndex: number,
    onValidate: (mediaBlock: MediaBlockFormikType) => Promise<void>,
    args?: SidebarNavigatorParams,
  ) => {
    document.querySelector('[data-slot="right-sidebar-context"]')?.scrollTo({ top: 0 })
    document.querySelector('[data-slot="right-sidebar-content"]')?.scrollTo({ top: 0 })
    document.querySelector('[data-slot="right-sidebar-content-stack-on-top"]')?.scrollTo({ top: 0 })

    addSidebarStackOnTop({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <MediaForm
              updatedMediaBlock={updatedMediaBlock}
              updatedMediaBlockIndex={updatedMediaBlockIndex}
              onValidate={onValidate}
            />
          </Suspense>
        </ErrorBoundary>
      ),
      ...args,
    })
  }

  const navigateToSingleBlockFormEdition = (
    block: SessionBlockEntityType | undefined,
    updatedBlockIndex: number,
    onValidated: (updatedBlock: BlockSessionInputDto) => Promise<void>,
  ) => {
    if (!block) throw new Error("Block not found")

    const blockFormik = SessionBlockEntityFactory.fromEntityToFormik(block)

    if (blockFormik.type === SessionBlockType.SESSION_EXERCISE) {
      navigateToExerciseForm(
        blockFormik.content,
        updatedBlockIndex,
        async (exercise: ExerciseFormikType) => {
          const newBlock = {
            type: SessionBlockType.SESSION_EXERCISE,
            content: SessionBlockExerciseEntity.fromFormikToInputDto(exercise, updatedBlockIndex),
          }
          await onValidated(newBlock)
        },
        { replaceAllComponents: true },
      )
    } else if (blockFormik.type === SessionBlockType.COACH_NOTE) {
      navigateToNoteForCoachesForm(
        blockFormik.content,
        updatedBlockIndex,
        async (note: CoachNoteFormikType) => {
          const newBlock = {
            type: SessionBlockType.COACH_NOTE,
            content: SessionBlockCoachNoteEntity.fromFormikToInputDto(note, updatedBlockIndex),
          }
          await onValidated(newBlock)
        },
        { replaceAllComponents: true },
      )
    } else if (blockFormik.type === SessionBlockType.ATHLETE_NOTE) {
      navigateToNoteForAthletesForm(
        blockFormik.content,
        updatedBlockIndex,
        async (note: AthleteNoteFormikType) => {
          const newBlock = {
            type: SessionBlockType.ATHLETE_NOTE,
            content: SessionBlockAthleteNoteEntity.fromFormikToInputDto(note, updatedBlockIndex),
          }
          await onValidated(newBlock)
        },
        { replaceAllComponents: true },
      )
    } else /* if (blockFormik.type === SessionBlockType.MEDIAS) */ {
      navigateToMediaForm(
        blockFormik.content,
        updatedBlockIndex,
        async (mediaBlock: MediaBlockFormikType) => {
          const newBlock = {
            type: SessionBlockType.MEDIAS,
            content: SessionBlockMediaEntity.fromFormikToInputDto(mediaBlock, updatedBlockIndex),
          }
          await onValidated(newBlock)
        },
        { replaceAllComponents: true },
      )
    }
  }

  const navigateToSingleBlockFormCreation = (
    updatedBlockIndex: number,
    newBlockType: SessionBlockType,
    onValidated: (newBlock: BlockSessionInputDto) => Promise<void>,
  ) => {
    if (newBlockType === SessionBlockType.SESSION_EXERCISE) {
      navigateToExerciseForm(
        undefined,
        updatedBlockIndex,
        async (exercise: ExerciseFormikType) => {
          const newBlock = {
            type: newBlockType,
            content: SessionBlockExerciseEntity.fromFormikToInputDto(exercise, updatedBlockIndex),
          }
          await onValidated(newBlock)
        },
        { replaceAllComponents: true },
      )
    } else if (newBlockType === SessionBlockType.COACH_NOTE) {
      navigateToNoteForCoachesForm(
        undefined,
        updatedBlockIndex,
        async (note: CoachNoteFormikType) => {
          const newBlock = {
            type: newBlockType,
            content: SessionBlockCoachNoteEntity.fromFormikToInputDto(note, updatedBlockIndex),
          }
          await onValidated(newBlock)
        },
        { replaceAllComponents: true },
      )
    } else if (newBlockType === SessionBlockType.ATHLETE_NOTE) {
      navigateToNoteForAthletesForm(
        undefined,
        updatedBlockIndex,
        async (note: AthleteNoteFormikType) => {
          const newBlock = {
            type: newBlockType,
            content: SessionBlockAthleteNoteEntity.fromFormikToInputDto(note, updatedBlockIndex),
          }
          await onValidated(newBlock)
        },
        { replaceAllComponents: true },
      )
    } else /* if (newBlockType === SessionBlockType.MEDIAS) */ {
      navigateToMediaForm(
        undefined,
        updatedBlockIndex,
        async (mediaBlock: MediaBlockFormikType) => {
          const newBlock = {
            type: newBlockType,
            content: SessionBlockMediaEntity.fromFormikToInputDto(mediaBlock, updatedBlockIndex),
          }
          await onValidated(newBlock)
        },
        { replaceAllComponents: true },
      )
    }
  }

  return {
    navigateToSessionView,
    navigateToSessionForm,
    navigateToExerciseForm,
    navigateToNoteForCoachesForm,
    navigateToNoteForAthletesForm,
    navigateToMediaForm,
    navigateToSingleBlockFormEdition,
    navigateToSingleBlockFormCreation,
    sidebarNavigateBack,
    isSidebarVisible,
  }
}
